// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================

import React, { useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';

const INDUSTRIES = [
  { key: 'agriculture', name: 'Agriculture', emoji: '\u{1F33E}', deployed: 3 },
  { key: 'fleet', name: 'Fleet / Logistics', emoji: '\u{1F69A}', deployed: 1 },
  { key: 'directional_drilling', name: 'Directional Drilling', emoji: '\u{1F3AF}', deployed: 1 },
  { key: 'auto_dealership', name: 'Auto Dealership', emoji: '\u{1F697}', deployed: 1 },
  { key: 'healthcare', name: 'Healthcare', emoji: '\u{1FA7A}', deployed: 2 },
  { key: 'cannabis', name: 'Cannabis', emoji: '\u{1F33F}', deployed: 1 },
  { key: 'mining', name: 'Mining', emoji: '\u{26CF}', deployed: 1 },
  { key: 'vac_hauling', name: 'Vac Hauling', emoji: '\u{1F6E2}', deployed: 1 },
  { key: 'beauty', name: 'Beauty', emoji: '\u{1F484}', deployed: 1 },
  { key: 'real_estate', name: 'Real Estate', emoji: '\u{1F3E0}', deployed: 1 },
  { key: 'mechanic', name: 'Mechanic', emoji: '\u{1F527}', deployed: 1 },
  { key: 'tire_shop', name: 'Tire Shop', emoji: '\u{1F6DE}', deployed: 1 },
  { key: 'cattle', name: 'Cattle', emoji: '\u{1F404}', deployed: 1 },
  { key: 'wellness', name: 'Wellness', emoji: '\u{1F9D8}', deployed: 1 },
  { key: 'insurance', name: 'Insurance', emoji: '\u{1F6E1}', deployed: 1 },
  { key: 'event_venue', name: 'Event Venue', emoji: '\u{1F3AA}', deployed: 1 },
  { key: 'paperwork', name: 'Paperwork / Forms', emoji: '\u{1F4DD}', deployed: 1 },
  { key: 'iron_steel', name: 'Iron / Steel', emoji: '\u{2699}', deployed: 1 },
  { key: 'hoist_crane', name: 'Hoist / Crane', emoji: '\u{1F3D7}', deployed: 1 },
  { key: 'rig_safety', name: 'Rig Safety', emoji: '\u{26A0}', deployed: 1 },
  { key: 'field_service', name: 'Field Service', emoji: '\u{1F6E0}', deployed: 1 },
  { key: 'internal_tools', name: 'Internal Tools', emoji: '\u{1F4CA}', deployed: 1 },
  { key: 'custom', name: 'Custom', emoji: '\u{2728}', deployed: 0 },
];

const PRESET_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444',
  '#8b5cf6', '#ec4899', '#06b6d4', '#f97316',
];

const FEATURES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'customer_management', label: 'Customer Management' },
  { key: 'invoicing', label: 'Invoicing' },
  { key: 'compliance', label: 'Compliance Tracking' },
  { key: 'reports', label: 'Reports' },
  { key: 'dispatch', label: 'Field Dispatch' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'scheduling', label: 'Scheduling' },
  { key: 'documents', label: 'Document Management' },
];

const TIERS = [
  {
    key: 'starter',
    name: 'Starter',
    price: 10000,
    monthly: 199,
    delivery: 7,
    maxRoles: 2,
    features: [
      'Template-based build',
      'Standard feature set',
      'Up to 2 user roles',
      'Email support',
      'Delivered in 7 days',
    ],
  },
  {
    key: 'professional',
    name: 'Professional',
    price: 35000,
    monthly: 499,
    delivery: 14,
    maxRoles: 4,
    popular: true,
    features: [
      'Custom feature configuration',
      'API integrations',
      'Up to 4 user roles',
      'Priority support',
      'Delivered in 14 days',
    ],
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price: 75000,
    monthly: 999,
    delivery: 21,
    maxRoles: 99,
    features: [
      'Full custom build',
      'Unlimited user roles',
      'Dedicated account manager',
      'SLA guarantee',
      'Delivered in 21 days',
    ],
  },
];

const DEMO_BUILDS = [
  {
    client: 'Summit Drilling Co.',
    industry: 'Directional Drilling',
    tier: 'Enterprise',
    status: 'Testing',
    progress: 72,
    eta: 'Feb 18, 2026',
  },
  {
    client: 'Prairie Ag Solutions',
    industry: 'Agriculture',
    tier: 'Professional',
    progress: 45,
    status: 'Building',
    eta: 'Feb 24, 2026',
  },
  {
    client: 'Metro Fleet Services',
    industry: 'Fleet / Logistics',
    tier: 'Starter',
    progress: 90,
    status: 'Deploying',
    eta: 'Feb 14, 2026',
  },
];

const BUILD_STAGES = ['Configuring', 'Building', 'Testing', 'Deploying', 'Live'];

function getStageIndex(status) {
  const idx = BUILD_STAGES.indexOf(status);
  return idx === -1 ? 0 : idx;
}

function formatCurrency(num) {
  return '$' + num.toLocaleString('en-US');
}

export default function AppGeneratorPage() {
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#6366f1');
  const [customColor, setCustomColor] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState(['dashboard', 'customer_management', 'invoicing']);
  const [roleCount, setRoleCount] = useState(2);
  const [selectedTier, setSelectedTier] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const steps = [
    { num: 1, label: 'Industry' },
    { num: 2, label: 'Configure' },
    { num: 3, label: 'Pricing' },
    { num: 4, label: 'Review' },
  ];

  const canProceed = () => {
    if (currentStep === 1) return selectedIndustry !== null;
    if (currentStep === 2) return brandName.trim().length > 0;
    if (currentStep === 3) return selectedTier !== null;
    return true;
  };

  const handleFeatureToggle = (key) => {
    setSelectedFeatures((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const handleColorSelect = (color) => {
    setPrimaryColor(color);
    setCustomColor('');
  };

  const handleCustomColorChange = (e) => {
    const val = e.target.value;
    setCustomColor(val);
    if (/^#[0-9a-fA-F]{6}$/.test(val)) {
      setPrimaryColor(val);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const tier = TIERS.find((t) => t.key === selectedTier);
      const industry = INDUSTRIES.find((ind) => ind.key === selectedIndustry);
      const payload = {
        user_id: user?.id || null,
        user_email: user?.email || null,
        industry: industry?.name || selectedIndustry,
        brand_name: brandName.trim(),
        primary_color: primaryColor,
        features: selectedFeatures,
        role_count: roleCount,
        tier: selectedTier,
        upfront_price: tier?.price || 0,
        monthly_price: tier?.monthly || 0,
        delivery_days: tier?.delivery || 0,
        status: 'pending',
        created_at: new Date().toISOString(),
      };

      const { error } = await supabase.from('app_builds').insert([payload]);

      if (error) {
        console.error('Build request save error:', error);
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const industryObj = INDUSTRIES.find((ind) => ind.key === selectedIndustry);
  const tierObj = TIERS.find((t) => t.key === selectedTier);

  // ---------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------

  const renderStepIndicator = () => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 0, marginBottom: 36, flexWrap: 'wrap',
    }}>
      {steps.map((s, i) => (
        <React.Fragment key={s.num}>
          <div
            onClick={() => { if (s.num < currentStep) setCurrentStep(s.num); }}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              cursor: s.num < currentStep ? 'pointer' : 'default',
              opacity: s.num <= currentStep ? 1 : 0.4,
              minWidth: 80,
            }}
          >
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 16,
              background: s.num === currentStep ? '#6366f1' : s.num < currentStep ? '#10b981' : 'var(--card)',
              color: s.num <= currentStep ? '#fff' : 'var(--text-muted)',
              border: s.num === currentStep ? '2px solid #6366f1' : s.num < currentStep ? '2px solid #10b981' : '2px solid var(--border)',
              transition: 'all 0.2s ease',
            }}>
              {s.num < currentStep ? '\u2713' : s.num}
            </div>
            <span style={{
              fontSize: 12, fontWeight: 600, marginTop: 6,
              color: s.num === currentStep ? '#6366f1' : s.num < currentStep ? '#10b981' : 'var(--text-muted)',
            }}>
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div style={{
              flex: 1, height: 2, minWidth: 40, maxWidth: 100,
              background: s.num < currentStep ? '#10b981' : 'var(--border)',
              margin: '0 8px', marginBottom: 20,
              transition: 'background 0.2s ease',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderNavButtons = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32 }}>
      {currentStep > 1 ? (
        <button
          onClick={() => setCurrentStep((s) => s - 1)}
          style={{
            padding: '12px 28px', borderRadius: 8, border: '1px solid var(--border)',
            background: 'var(--card)', color: 'var(--text)', fontSize: 15,
            fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s ease',
          }}
        >
          Back
        </button>
      ) : (
        <div />
      )}
      {currentStep < 4 && (
        <button
          onClick={() => { if (canProceed()) setCurrentStep((s) => s + 1); }}
          disabled={!canProceed()}
          style={{
            padding: '12px 28px', borderRadius: 8, border: 'none',
            background: canProceed() ? '#6366f1' : '#3f3f6a',
            color: '#fff', fontSize: 15, fontWeight: 600,
            cursor: canProceed() ? 'pointer' : 'not-allowed',
            opacity: canProceed() ? 1 : 0.5,
            transition: 'all 0.15s ease',
          }}
        >
          Continue
        </button>
      )}
    </div>
  );

  // Step 1 — Choose Industry
  const renderStep1 = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
        Step 1: Choose Your Industry
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
        Select the industry vertical that best matches your business. We have proven templates for each.
      </p>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
        gap: 14,
      }}>
        {INDUSTRIES.map((ind) => {
          const isSelected = selectedIndustry === ind.key;
          return (
            <div
              key={ind.key}
              onClick={() => setSelectedIndustry(ind.key)}
              style={{
                padding: 18, borderRadius: 12, cursor: 'pointer',
                background: 'var(--card)',
                border: isSelected ? '2px solid #6366f1' : '2px solid var(--border)',
                boxShadow: isSelected ? '0 0 0 3px rgba(99,102,241,0.2)' : 'none',
                transition: 'all 0.15s ease',
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8 }}>{ind.emoji}</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
                {ind.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {ind.deployed} app{ind.deployed !== 1 ? 's' : ''} deployed
              </div>
            </div>
          );
        })}
      </div>
      {renderNavButtons()}
    </div>
  );

  // Step 2 — Configure Your App
  const renderStep2 = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
        Step 2: Configure Your App
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
        Customize the branding and features for your new application.
      </p>

      {/* Brand Name */}
      <div style={{ marginBottom: 28 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Brand Name <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="text"
          value={brandName}
          onChange={(e) => setBrandName(e.target.value)}
          placeholder="e.g. Summit Drilling Ops"
          style={{
            width: '100%', maxWidth: 440, padding: '12px 16px', borderRadius: 8,
            border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)',
            fontSize: 15, outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Primary Color */}
      <div style={{ marginBottom: 28 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Primary Color
        </label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          {PRESET_COLORS.map((c) => (
            <div
              key={c}
              onClick={() => handleColorSelect(c)}
              style={{
                width: 36, height: 36, borderRadius: 8, background: c, cursor: 'pointer',
                border: primaryColor === c ? '3px solid #fff' : '3px solid transparent',
                boxShadow: primaryColor === c ? '0 0 0 2px #6366f1' : 'none',
                transition: 'all 0.15s ease',
              }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            type="text"
            value={customColor}
            onChange={handleCustomColorChange}
            placeholder="Custom hex: #ff6600"
            style={{
              padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg)', color: 'var(--text)', fontSize: 13, width: 180,
              outline: 'none',
            }}
          />
          <div style={{
            width: 36, height: 36, borderRadius: 8, background: primaryColor,
            border: '1px solid var(--border)', flexShrink: 0,
          }} />
        </div>
      </div>

      {/* Logo Upload Placeholder */}
      <div style={{ marginBottom: 28 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Logo
        </label>
        <div style={{
          width: '100%', maxWidth: 440, padding: 28, borderRadius: 12,
          border: '2px dashed var(--border)', background: 'var(--card)',
          textAlign: 'center', cursor: 'pointer',
        }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>{'\u{1F4E4}'}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 4 }}>
            Drag & drop your logo or click to browse
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            PNG, SVG, or JPG (max 2 MB)
          </div>
        </div>
      </div>

      {/* Feature Checklist */}
      <div style={{ marginBottom: 28 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>
          Features
        </label>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 10,
        }}>
          {FEATURES.map((f) => {
            const checked = selectedFeatures.includes(f.key);
            return (
              <label
                key={f.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  borderRadius: 8, background: 'var(--card)',
                  border: checked ? '1px solid #6366f1' : '1px solid var(--border)',
                  cursor: 'pointer', transition: 'border 0.15s ease',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleFeatureToggle(f.key)}
                  style={{ accentColor: '#6366f1', width: 16, height: 16 }}
                />
                <span style={{ fontSize: 14, color: 'var(--text)' }}>{f.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Role Count Slider */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Number of User Roles: <span style={{ color: '#6366f1' }}>{roleCount}</span>
        </label>
        <input
          type="range"
          min={1}
          max={5}
          value={roleCount}
          onChange={(e) => setRoleCount(Number(e.target.value))}
          style={{ width: '100%', maxWidth: 440, accentColor: '#6366f1' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: 440, fontSize: 11, color: 'var(--text-muted)' }}>
          <span>1 role</span>
          <span>5 roles</span>
        </div>
      </div>

      {renderNavButtons()}
    </div>
  );

  // Step 3 — Choose Your Tier
  const renderStep3 = () => (
    <div>
      <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
        Step 3: Choose Your Tier
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 8 }}>
        Every plan includes deployment, hosting, and ongoing maintenance.
      </p>
      <p style={{ color: '#ef4444', fontSize: 13, fontWeight: 600, marginBottom: 28 }}>
        Monthly hosting is required for all plans.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: 20, alignItems: 'stretch',
      }}>
        {TIERS.map((tier) => {
          const isSelected = selectedTier === tier.key;
          return (
            <div
              key={tier.key}
              onClick={() => setSelectedTier(tier.key)}
              style={{
                padding: 28, borderRadius: 14, background: 'var(--card)',
                border: isSelected ? '2px solid #6366f1' : '2px solid var(--border)',
                boxShadow: isSelected ? '0 0 0 3px rgba(99,102,241,0.2)' : 'none',
                cursor: 'pointer', transition: 'all 0.15s ease',
                position: 'relative', display: 'flex', flexDirection: 'column',
              }}
            >
              {tier.popular && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: '#6366f1', color: '#fff', fontSize: 11, fontWeight: 700,
                  padding: '4px 14px', borderRadius: 20, textTransform: 'uppercase',
                  letterSpacing: 0.5, whiteSpace: 'nowrap',
                }}>
                  Most Popular
                </div>
              )}

              <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
                {tier.name}
              </div>

              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 36, fontWeight: 800, color: '#6366f1' }}>
                  {formatCurrency(tier.price)}
                </span>
                <span style={{ fontSize: 14, color: 'var(--text-muted)', marginLeft: 4 }}>
                  one-time
                </span>
              </div>

              <div style={{
                padding: '10px 14px', borderRadius: 8,
                background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)',
                marginBottom: 18, textAlign: 'center',
              }}>
                <span style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>
                  {formatCurrency(tier.monthly)}/mo
                </span>
                <span style={{ fontSize: 12, color: '#ef4444', display: 'block', marginTop: 2 }}>
                  hosting fee (required)
                </span>
              </div>

              <div style={{
                background: 'rgba(99,102,241,0.08)', borderRadius: 8, padding: '8px 14px',
                marginBottom: 18, textAlign: 'center',
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#6366f1' }}>
                  Delivered in {tier.delivery} days
                </span>
              </div>

              <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1 }}>
                {tier.features.map((feat, idx) => (
                  <li key={idx} style={{
                    fontSize: 13, color: 'var(--text)', padding: '6px 0',
                    borderBottom: idx < tier.features.length - 1 ? '1px solid var(--border)' : 'none',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{ color: '#10b981', fontSize: 14 }}>{'\u2713'}</span>
                    {feat}
                  </li>
                ))}
              </ul>

              <button
                style={{
                  marginTop: 20, padding: '12px 0', borderRadius: 8, border: 'none',
                  background: isSelected ? '#6366f1' : 'transparent',
                  color: isSelected ? '#fff' : '#6366f1', fontSize: 14, fontWeight: 700,
                  cursor: 'pointer', width: '100%',
                  outline: isSelected ? 'none' : '2px solid #6366f1',
                  transition: 'all 0.15s ease',
                }}
              >
                {isSelected ? 'Selected' : 'Select Plan'}
              </button>
            </div>
          );
        })}
      </div>

      {renderNavButtons()}
    </div>
  );

  // Step 4 — Review & Submit
  const renderStep4 = () => {
    if (submitted) {
      return (
        <div style={{
          textAlign: 'center', padding: '60px 20px',
          background: 'var(--card)', borderRadius: 14,
          border: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>{'\u{1F389}'}</div>
          <h2 style={{ color: 'var(--text)', fontSize: 26, fontWeight: 700, marginBottom: 8 }}>
            Build Request Submitted!
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 15, maxWidth: 480, margin: '0 auto 24px' }}>
            Our team will contact you within 24 hours to begin your build. You can track progress in the Active Builds section below.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setCurrentStep(1);
              setSelectedIndustry(null);
              setBrandName('');
              setPrimaryColor('#6366f1');
              setCustomColor('');
              setSelectedFeatures(['dashboard', 'customer_management', 'invoicing']);
              setRoleCount(2);
              setSelectedTier(null);
            }}
            style={{
              padding: '12px 28px', borderRadius: 8, border: 'none',
              background: '#6366f1', color: '#fff', fontSize: 15,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Start Another Build
          </button>
        </div>
      );
    }

    return (
      <div>
        <h2 style={{ color: 'var(--text)', fontSize: 22, fontWeight: 700, marginBottom: 6 }}>
          Step 4: Review & Submit
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>
          Review your configuration before submitting. Our team will follow up within 24 hours.
        </p>

        <div style={{
          background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)',
          padding: 28, marginBottom: 24,
        }}>
          {/* Summary Grid */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 20, marginBottom: 24,
          }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                Industry
              </div>
              <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600 }}>
                {industryObj ? `${industryObj.emoji} ${industryObj.name}` : 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                Brand Name
              </div>
              <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600 }}>
                {brandName || 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                Primary Color
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 22, height: 22, borderRadius: 6, background: primaryColor, border: '1px solid var(--border)' }} />
                <span style={{ fontSize: 14, color: 'var(--text)', fontFamily: 'monospace' }}>{primaryColor}</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                Tier
              </div>
              <div style={{ fontSize: 16, color: '#6366f1', fontWeight: 700 }}>
                {tierObj ? tierObj.name : 'N/A'}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                User Roles
              </div>
              <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600 }}>
                {roleCount}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                Estimated Delivery
              </div>
              <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600 }}>
                {tierObj ? `${tierObj.delivery} days` : 'N/A'}
              </div>
            </div>
          </div>

          {/* Features */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Selected Features
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {selectedFeatures.map((fKey) => {
                const feat = FEATURES.find((f) => f.key === fKey);
                return (
                  <span
                    key={fKey}
                    style={{
                      padding: '5px 12px', borderRadius: 20, background: 'rgba(99,102,241,0.12)',
                      color: '#6366f1', fontSize: 12, fontWeight: 600,
                    }}
                  >
                    {feat ? feat.label : fKey}
                  </span>
                );
              })}
              {selectedFeatures.length === 0 && (
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No features selected</span>
              )}
            </div>
          </div>

          {/* Pricing Summary */}
          <div style={{
            background: 'var(--bg)', borderRadius: 10, padding: 20,
            border: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 14 }}>
              Pricing Summary
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>One-time build fee</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
                {tierObj ? formatCurrency(tierObj.price) : '--'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>Monthly hosting</span>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>
                {tierObj ? `${formatCurrency(tierObj.monthly)}/mo` : '--'}
              </span>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>Due today</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#6366f1' }}>
                  {tierObj ? formatCurrency(tierObj.price) : '--'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Then monthly</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#ef4444' }}>
                  {tierObj ? `${formatCurrency(tierObj.monthly)}/mo` : '--'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Submit CTA */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => setCurrentStep(3)}
            style={{
              padding: '12px 28px', borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--card)', color: 'var(--text)', fontSize: 15,
              fontWeight: 600, cursor: 'pointer',
            }}
          >
            Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '14px 36px', borderRadius: 8, border: 'none',
              background: submitting ? '#4f46e5' : '#6366f1', color: '#fff',
              fontSize: 16, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Build Request'}
          </button>
        </div>

        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, marginTop: 16 }}>
          Our team will contact you within 24 hours to begin your build.
        </p>
      </div>
    );
  };

  // Active Builds Tracker
  const renderActiveBuilds = () => (
    <div style={{ marginTop: 48 }}>
      <h2 style={{ color: 'var(--text)', fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
        Active Builds
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
        Track the progress of apps currently being built.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {DEMO_BUILDS.map((build, idx) => {
          const stageIdx = getStageIndex(build.status);
          return (
            <div
              key={idx}
              style={{
                background: 'var(--card)', borderRadius: 12, border: '1px solid var(--border)',
                padding: 20,
              }}
            >
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                flexWrap: 'wrap', gap: 12, marginBottom: 16,
              }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                    {build.client}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    {build.industry} &middot; {build.tier}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    ETA: {build.eta}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#6366f1' }}>
                    {build.progress}%
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{
                width: '100%', height: 8, background: 'var(--bg)',
                borderRadius: 4, overflow: 'hidden', marginBottom: 12,
              }}>
                <div style={{
                  width: `${build.progress}%`, height: '100%',
                  background: build.progress >= 90 ? '#10b981' : '#6366f1',
                  borderRadius: 4, transition: 'width 0.4s ease',
                }} />
              </div>

              {/* Stage Indicators */}
              <div style={{
                display: 'flex', justifyContent: 'space-between', gap: 4,
              }}>
                {BUILD_STAGES.map((stage, sIdx) => {
                  const isComplete = sIdx < stageIdx;
                  const isCurrent = sIdx === stageIdx;
                  return (
                    <div
                      key={stage}
                      style={{
                        flex: 1, textAlign: 'center', padding: '6px 4px',
                        borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: isCurrent
                          ? 'rgba(99,102,241,0.15)'
                          : isComplete
                            ? 'rgba(16,185,129,0.1)'
                            : 'transparent',
                        color: isCurrent
                          ? '#6366f1'
                          : isComplete
                            ? '#10b981'
                            : 'var(--text-muted)',
                      }}
                    >
                      {isComplete ? '\u2713 ' : ''}{stage}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ---------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------
  return (
    <div className="page-body" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      {/* Hero Header */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <h1 style={{
          color: 'var(--text)', fontSize: 32, fontWeight: 800,
          margin: '0 0 8px', lineHeight: 1.2,
        }}>
          App Generator
        </h1>
        <p style={{
          color: 'var(--text-muted)', fontSize: 16, margin: 0,
          maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
        }}>
          Turn your idea into a production app. We build it in days, not months.
        </p>
      </div>

      {/* Step Indicator */}
      {!submitted && renderStepIndicator()}

      {/* Step Content */}
      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}
      {currentStep === 4 && renderStep4()}

      {/* Active Builds Tracker */}
      {renderActiveBuilds()}
    </div>
  );
}
